/**
 * Guide admin on which staging edit fields need attention vs already filled.
 */

const FIELD_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  mobile: 'Phone / mobile',
  whatsapp: 'WhatsApp',
  gender: 'Gender',
  dob: 'Date of birth',
  veStudentId: 'VE student ID',
  employeeId: 'Employee ID',
  address: 'Current address',
  permanentAddress: 'Permanent address',
  pincode: 'Pincode',
  parentsName: 'Parents name',
  parentsPhone: 'Parents phone',
  parentsEmail: 'Parents email',
  batchId: 'Batch',
  courseId: 'Course',
  domainId: 'Domain',
  degree: 'Degree',
  university: 'University / institute',
  btechYear: 'B.Tech year',
  linkedIn: 'LinkedIn',
  country: 'Country',
  state: 'State',
  cityId: 'City',
};

/** Review reason / warning → form fields that should be highlighted. */
const REASON_FIELDS = {
  missing_email: ['email'],
  incomplete_first_name: ['firstName'],
  incomplete_last_name: ['lastName'],
  incomplete_phone: ['mobile'],
  ambiguous_first_name: ['firstName'],
  ambiguous_last_name: ['lastName'],
  duplicate_email_in_file: ['email'],
  existing_student_update: ['email'],
  existing_student_field_discrepancy: [],
  ambiguous_or_missing_batch: ['batchId'],
  missing_batch: ['batchId'],
  ambiguous_batch: ['batchId'],
  fee_without_enrollment: ['batchId'],
  new_enrollment_for_existing_student: ['batchId'],
  has_unmapped_fields: [],
};

const REQUIRED_FOR_APPLY = ['email', 'firstName', 'lastName', 'mobile'];

function isFilled(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return true;
  return String(value).trim() !== '';
}

function attentionFieldsFromDoc(doc) {
  const rr = doc?.ruleResult || {};
  const set = new Set();
  for (const reason of rr.reviewReasons || []) {
    for (const f of REASON_FIELDS[reason] || []) set.add(f);
  }
  for (const w of rr.warnings || []) {
    if (/country not found/i.test(w)) set.add('country');
    if (/state not found/i.test(w)) set.add('state');
    if (/city not found/i.test(w)) set.add('cityId');
    if (/phone/i.test(w)) set.add('mobile');
    if (/batch/i.test(w)) set.add('batchId');
  }
  for (const c of rr.conflicts || []) {
    if (/firstName|first name/i.test(c)) set.add('firstName');
    if (/lastName|last name/i.test(c)) set.add('lastName');
    if (/email/i.test(c)) set.add('email');
    if (/batch/i.test(c)) set.add('batchId');
    if (/duplicate/i.test(c)) set.add('email');
  }
  const discrepancyToForm = {
    phoneNumber: 'mobile',
    currentAddress: 'address',
    firstName: 'firstName',
    lastName: 'lastName',
    fullName: 'firstName',
    gender: 'gender',
    parentsName: 'parentsName',
    parentsPhone: 'parentsPhone',
    parentsEmail: 'parentsEmail',
    veStudentId: 'veStudentId',
    linkedIn: 'linkedIn',
    domainId: 'domainId',
    cityId: 'cityId',
  };
  for (const d of rr.discrepancies || []) {
    const formField = discrepancyToForm[d.field] || d.field;
    if (FIELD_LABELS[formField] || discrepancyToForm[d.field]) set.add(formField);
  }
  return set;
}

/**
 * @param {object} formData admin form shape
 * @param {object|null} doc staging document
 * @param {{ focusFormFields?: string[] }} [opts] — completeness field chips force-highlight these form fields
 */
export function buildStagingFieldGuidance(formData, doc, opts = {}) {
  const attentionSet = attentionFieldsFromDoc(doc);
  for (const f of opts.focusFormFields || []) {
    if (f) attentionSet.add(f);
  }
  const attention = [];
  const filled = [];
  const emptyOptional = [];
  const byField = {};

  const consider = (field, { required = false } = {}) => {
    const value = formData?.[field];
    const label = FIELD_LABELS[field] || field;
    const filledOk = isFilled(value);
    const needsAttention =
      attentionSet.has(field) || (required && !filledOk) || (REQUIRED_FOR_APPLY.includes(field) && !filledOk);

    if (needsAttention && !filledOk) {
      byField[field] = 'missing';
      attention.push({ field, label, value: '', status: 'missing' });
    } else if (needsAttention && filledOk) {
      byField[field] = 'fixed';
      filled.push({ field, label, value: String(value), status: 'fixed' });
    } else if (filledOk) {
      byField[field] = 'ok';
      filled.push({ field, label, value: String(value), status: 'ok' });
    } else {
      byField[field] = 'empty';
      emptyOptional.push({ field, label, status: 'empty' });
    }
  };

  for (const f of REQUIRED_FOR_APPLY) consider(f, { required: true });
  const optionalFields = [
    'gender',
    'whatsapp',
    'dob',
    'veStudentId',
    'employeeId',
    'address',
    'permanentAddress',
    'pincode',
    'parentsName',
    'parentsPhone',
    'parentsEmail',
    'batchId',
    'courseId',
    'modeOfCourse',
    'domainId',
    'degree',
    'university',
    'btechInstitute',
    'btechYear',
    'yearOfPassing',
    'btechMarks',
    'linkedIn',
    'profilePreference',
    'joiningKitReceived',
    'heardFrom',
    'country',
    'state',
    'cityId',
  ];
  for (const f of optionalFields) {
    if (!REQUIRED_FOR_APPLY.includes(f)) consider(f);
  }
  // Ensure focus-only fields still appear in byField even if not in the lists above
  for (const f of opts.focusFormFields || []) {
    if (f && byField[f] === undefined) consider(f);
  }

  // Deduplicate attention/filled by field
  const uniq = (arr) => {
    const seen = new Set();
    return arr.filter((x) => {
      if (seen.has(x.field)) return false;
      seen.add(x.field);
      return true;
    });
  };

  return {
    byField,
    attention: uniq(attention),
    filled: uniq(filled),
    emptyOptional: uniq(emptyOptional),
    legend: {
      missing: 'Needs attention (empty or flagged by review rules)',
      ok: 'Filled from import / rules',
      empty: 'Optional — still empty',
    },
  };
}

/** Field style for AdminUserBasicInfoSection / CourseSelection (matches edit-user yellow empties + staging accents). */
export function createStagingGetFieldStyle(guidance) {
  return (fieldValue, isRequired = false, fieldName) => {
    const status = fieldName ? guidance?.byField?.[fieldName] : undefined;
    const empty = !isFilled(fieldValue);
    const flagged = status === 'missing' || isRequired;

    // Still empty + required/flagged → red
    if (empty && flagged) {
      return {
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
        border: '2px solid rgba(239, 68, 68, 0.55)',
        color: 'var(--color-text)',
      };
    }
    // Has a value → green (filled correctly / fixed)
    if (!empty) {
      return {
        backgroundColor: 'rgba(34, 197, 94, 0.08)',
        border: '2px solid rgba(34, 197, 94, 0.35)',
        color: 'var(--color-text)',
      };
    }
    // Optional empty → amber
    return {
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
      border: '2px solid rgba(255, 193, 7, 0.35)',
      color: 'var(--color-text)',
    };
  };
}

/** CSS class helper for plain inputs in inline edit. */
export function stagingFieldClass(guidance, fieldName, base = '') {
  const status = guidance?.byField?.[fieldName];
  if (status === 'missing') return `${base} ring-2 ring-red-400/70 bg-red-50`.trim();
  if (status === 'ok' || status === 'fixed') return `${base} ring-2 ring-green-400/50 bg-green-50`.trim();
  return `${base} ring-1 ring-amber-300/60 bg-amber-50/40`.trim();
}
